/**
 * ECPatterns Component
 *
 * Displays EC (Elimination Communication) patterns as a stacked bar chart.
 * Shows daily pee/poop outcomes (none/little/loads) over a selectable period.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { eventApi } from '../services/api';
import { getBabyKey, decryptEventData } from '../crypto';
import { format, subDays } from 'date-fns';

interface ECPatternsProps {
  babyId: string;
}

type Quantity = 'none' | 'little' | 'loads';

// Aggregate EC events by day
function aggregateByDay(events: Array<{ timestamp: Date; ecPee: Quantity; ecPoop: Quantity }>) {
  const byDay: Record<string, { date: string; peeNone: number; peeLittle: number; peeLoads: number; poopNone: number; poopLittle: number; poopLoads: number; total: number }> = {};

  for (const ev of events) {
    const day = format(ev.timestamp, 'MM/dd');
    if (!byDay[day]) {
      byDay[day] = { date: day, peeNone: 0, peeLittle: 0, peeLoads: 0, poopNone: 0, poopLittle: 0, poopLoads: 0, total: 0 };
    }
    byDay[day].total++;
    if (ev.ecPee === 'none') byDay[day].peeNone++;
    else if (ev.ecPee === 'little') byDay[day].peeLittle++;
    else if (ev.ecPee === 'loads') byDay[day].peeLoads++;
    if (ev.ecPoop === 'none') byDay[day].poopNone++;
    else if (ev.ecPoop === 'little') byDay[day].poopLittle++;
    else if (ev.ecPoop === 'loads') byDay[day].poopLoads++;
  }

  return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
}

export default function ECPatterns({ babyId }: ECPatternsProps) {
  const [period, setPeriod] = useState<7 | 14 | 30>(7);
  const [ecEvents, setEcEvents] = useState<Array<{ timestamp: Date; ecPee: Quantity; ecPoop: Quantity }>>([]);

  const startDate = subDays(new Date(), period).toISOString();

  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['ec-events', babyId, period],
    queryFn: () => eventApi.list(babyId, { types: 'EC', startDate, limit: '500' }),
    enabled: !!babyId,
  });

  useEffect(() => {
    async function decrypt() {
      if (!eventsData?.data?.events || !babyId) return;
      const aesKey = await getBabyKey(babyId);
      if (!aesKey) return;

      const results: Array<{ timestamp: Date; ecPee: Quantity; ecPoop: Quantity }> = [];
      for (const ev of (eventsData.data.events as any[])) {
        try {
          const data = await decryptEventData(ev.encryptedData, aesKey);
          results.push({
            timestamp: new Date(ev.timestamp),
            ecPee: (data.ecPee as Quantity) || 'none',
            ecPoop: (data.ecPoop as Quantity) || 'none',
          });
        } catch {
          // skip
        }
      }
      setEcEvents(results);
    }
    decrypt();
  }, [eventsData, babyId]);

  const chartData = aggregateByDay(ecEvents);

  const totalSessions = ecEvents.length;
  const successfulPee = ecEvents.filter(e => e.ecPee !== 'none').length;
  const successfulPoop = ecEvents.filter(e => e.ecPoop !== 'none').length;

  return (
    <div className="card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          🚽 EC Patterns
        </h3>
        <div className="flex gap-2">
          {([7, 14, 30] as const).map(d => (
            <button key={d} onClick={() => setPeriod(d)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                period === d
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
        </div>
      ) : ecEvents.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <p className="text-3xl mb-2">🚽</p>
          <p>No EC sessions logged in the last {period} days</p>
        </div>
      ) : (
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{totalSessions}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sessions</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{successfulPee}</div>
              <div className="text-xs text-blue-500 dark:text-blue-400 mt-1">Pee catches</div>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{successfulPoop}</div>
              <div className="text-xs text-amber-500 dark:text-amber-400 mt-1">Poop catches</div>
            </div>
          </div>

          {/* Daily breakdown chart */}
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Daily sessions</p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--tooltip-bg, #fff)', border: '1px solid #e5e7eb', borderRadius: 8 }}
                  formatter={(val, name) => [val, name as string]}
                />
                <Legend />
                <Bar dataKey="peeLittle" name="Pee (little)" stackId="pee" fill="#93c5fd" />
                <Bar dataKey="peeLoads" name="Pee (loads)" stackId="pee" fill="#3b82f6" />
                <Bar dataKey="poopLittle" name="Poop (little)" stackId="poop" fill="#fcd34d" />
                <Bar dataKey="poopLoads" name="Poop (loads)" stackId="poop" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2 text-center">
              "None" sessions not shown — but they count! Every session helps build communication.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
